import {
  Shield,
  BadgeCheck,
  Banknote,
  CloudRain,
  Snowflake,
  Layers,
  Gauge,
} from "lucide-react";
import { AreaPageShell } from "./AreaPageShell";

export interface MetalFaq {
  question: string;
  answer: string;
}

export interface MetalPageData {
  city: string;
  citySlug: string;
  serviceName: string;
  title: string;
  description: string;
  h1: string;
  heroIntro: string;
  climateText: string;
  localPresenceText: string;
  warrantyText: string;
  pricingText: string;
  priceFactorsText: string;
  financingText: string;
  step1: string;
  step2: string;
  step3: string;
  step4: string;
  standingSeamText: string;
  materialsText: string;
  finishesText: string;
  faqs: MetalFaq[];
}

const buildSchema = (data: MetalPageData) => {
  const base = "https://www.saskroofingcompany.com";
  const url = `${base}/areas/${data.citySlug}/metal`;

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: data.faqs.map((f) => ({
      "@type": "Question",
      name: f.question,
      acceptedAnswer: { "@type": "Answer", text: f.answer },
    })),
  };

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "RoofingContractor",
    "@id": `${base}/#localbusiness`,
    name: "Sask Roofing Company",
    url: `${base}/`,
    telephone: "+1-639-739-7969",
    email: "info@saskroofingcompany.com",
    areaServed: {
      "@type": "City",
      name: data.city,
      containedInPlace: { "@type": "State", name: "Saskatchewan" },
    },
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${url}#service`,
    serviceType: "Metal Roofing Installation",
    name: "Metal Roofing",
    description: data.description,
    url,
    provider: {
      "@id": `${base}/#localbusiness`,
      "@type": "RoofingContractor",
      name: "Sask Roofing Company",
      url: `${base}/`,
      telephone: "+1-639-739-7969",
    },
    areaServed: { "@type": "City", name: data.city },
    offers: {
      "@type": "AggregateOffer",
      priceCurrency: "CAD",
      lowPrice: "15000",
      highPrice: "30000",
    },
    availableChannel: {
      "@type": "ServiceChannel",
      serviceUrl: `${base}/contact`,
      servicePhone: {
        "@type": "ContactPoint",
        telephone: "+1-639-739-7969",
        contactType: "customer service",
        areaServed: "CA-SK",
        availableLanguage: "English",
      },
    },
    termsOfService: `${base}/about#warranty`,
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "4.9",
      reviewCount: "87",
      bestRating: "5",
      worstRating: "1",
    },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${base}/` },
      {
        "@type": "ListItem",
        position: 2,
        name: "Service Areas",
        item: `${base}/areas`,
      },
      {
        "@type": "ListItem",
        position: 3,
        name: data.city,
        item: `${base}/areas/${data.citySlug}`,
      },
      { "@type": "ListItem", position: 4, name: "Metal Roofing", item: url },
    ],
  };

  return [breadcrumbSchema, localBusinessSchema, serviceSchema, faqSchema];
};

export function MetalPage({ data }: { data: MetalPageData }) {
  return (
    <AreaPageShell
      data={{
        ...data,
        schema: buildSchema(data),
        canonical: `/areas/${data.citySlug}/metal`,
      }}
      heroImage="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/67bfca73-3cfe-4fd0-b9fd-9ed4f4780799.png&output=webp&q=60"
      heroAlt={`Metal roofing in ${data.city}`}
      whyBlocks={[
        {
          icon: Snowflake,
          title: `Metal roofing sheds snow & resists hail in ${data.city}`,
          text: data.climateText,
        },
        {
          icon: Shield,
          title: `Local metal roofing crews in ${data.city}`,
          text: data.localPresenceText,
        },
        {
          icon: BadgeCheck,
          title: `Metal roofing warranties in ${data.city}`,
          text: data.warrantyText,
        },
      ]}
      costBlocks={[
        {
          title: `Metal roofing price range in ${data.city}`,
          text: data.pricingText,
        },
        {
          title: `What drives metal roof cost in ${data.city}`,
          text: data.priceFactorsText,
        },
        {
          title: `Metal roofing financing in ${data.city}`,
          text: data.financingText,
        },
      ]}
      extraCostTiles={[
        {
          label: "Metal installation",
          value: "$15K–$30K+",
          note: "standing seam / profiles",
        },
        {
          label: "Lifespan",
          value: "50+ yrs",
          note: "outlasts asphalt 2:1",
        },
        {
          label: "Hail rating",
          value: "Class 4",
          note: "impact-resistant",
        },
      ]}
      extraCallout={{
        icon: Banknote,
        title: `Metal roofing lifetime cost in ${data.city}`,
        text: "Metal costs more than asphalt up front, but it typically outlasts two full asphalt cycles and sheds the snow and ice that cause Saskatchewan ice damming. Over 50 years it often costs less in total.",
      }}
      processSteps={[
        {
          title: `Free metal roof inspection & measurement in ${data.city}`,
          text: data.step1,
        },
        {
          title: `Metal material selection & fabrication in ${data.city}`,
          text: data.step2,
        },
        {
          title: `Underlayment & metal panel install in ${data.city}`,
          text: data.step3,
        },
        {
          title: `Snow guards, flashing & walkthrough in ${data.city}`,
          text: data.step4,
        },
      ]}
      materialBlocks={[
        {
          icon: Layers,
          title: `Standing seam vs ribbed metal in ${data.city}`,
          text: data.standingSeamText,
        },
        {
          icon: Gauge,
          title: `Steel vs aluminum vs copper in ${data.city}`,
          text: data.materialsText,
        },
        {
          icon: CloudRain,
          title: `Metal roof paint finishes & colours in ${data.city}`,
          text: data.finishesText,
        },
      ]}
    />
  );
}
